<template>
  <div class="aki-row" :class="rowClass" data-testid="aki-compact-patient-row-wrapper">
    <div
      class="p-3 text-body-1 text-truncate cursor-pointer align-self-center"
      data-testid="aki-compact-patient-row"
      @click="redirectToPatientDetails"
    >
      <p class="text-primary font-semibold text-wrap">
        {{ getFormattedPatientName(patientData.case.patient) }}
      </p>
      <div v-if="isSidebarVisible" @click.stop>
        <ActionIcons
          :config="actionIconsConfig"
          @create-consultation-click="onCreateConsultation"
          @emergency-click="emit('emergency-click', caze.id)"
          @edit-patient-click="onEditPatient"
          @close-case-click="onCloseCase"
        />
      </div>
    </div>

    <VTooltip
      :text="akiPredictionTooltipText"
      :location="'bottom center'"
      :open-delay="500"
      :offset="-8"
      :disabled="!hasAcceptedAkiLimitation"
    >
      <template #activator="{ props: activatorProps }">
        <div
          v-bind="activatorProps"
          data-testid="aki-open-infobox-cell"
          class="justify-self-start align-self-center p-3 text-body-1 w-100"
          :class="{ 'cursor-pointer': hasAcceptedAkiLimitation }"
          @click="hasAcceptedAkiLimitation && openInfobox()"
        >
          <button
            v-if="!hasAcceptedAkiLimitation"
            data-testid="open-aki-sidebar-btn"
            class="mx-auto"
            @click="$emit('open-aki-sidebar')"
          >
            <VIcon
              class="material-icons-outlined"
              icon="visibility_off"
              size="18px"
              color="fg-disabled"
            />
          </button>
          <span
            v-else
            class="inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0"
            :style="akiRiskFlagStyle"
            data-testid="aki-risk-flag"
          />
        </div>
      </template>
    </VTooltip>

    <VTooltip
      :text="INV_BP.hasMeasurement ? INV_BP.displayDate : NON_INV_BP.displayDate"
      :location="'bottom center'"
      :open-delay="500"
      :offset="-8"
    >
      <template #activator="{ props: activatorProps }">
        <SystolicPressureDisplay
          v-bind="activatorProps"
          class="cursor-pointer"
          :systolic-pressure="INV_BP.hasMeasurement ? INV_BP : NON_INV_BP"
          :is-invasive="INV_BP.hasMeasurement"
          @click="openInfobox"
        />
      </template>
    </VTooltip>

    <VTooltip
      :text="HEART_RATE.displayDate"
      :location="'bottom center'"
      :open-delay="500"
      :offset="-8"
    >
      <template #activator="{ props: activatorProps }">
        <div
          v-bind="activatorProps"
          class="justify-self-start align-self-center p-3 text-body-1 w-100 cursor-pointer"
          @click="openInfobox"
        >
          {{ formatValue(HEART_RATE.displayValue, NumberFormat.INTEGER) }}
        </div>
      </template>
    </VTooltip>

    <VTooltip :text="RR.displayDate" :location="'bottom center'" :open-delay="500" :offset="-8">
      <template #activator="{ props: activatorProps }">
        <div
          v-bind="activatorProps"
          class="justify-self-start align-self-center p-3 text-body-1 w-100 cursor-pointer"
          @click="openInfobox"
        >
          {{ formatValue(RR.displayValue, NumberFormat.INTEGER) }}
        </div>
      </template>
    </VTooltip>

    <VTooltip
      :text="CREATININE.displayDate"
      :location="'bottom center'"
      :open-delay="500"
      :offset="-8"
    >
      <template #activator="{ props: activatorProps }">
        <div
          v-bind="activatorProps"
          class="justify-self-start align-self-center p-3 text-body-1 w-100 cursor-pointer"
          @click="openInfobox"
        >
          {{ formatValue(CREATININE.displayValue, NumberFormat.DECIMAL) }}
        </div>
      </template>
    </VTooltip>

    <VTooltip :text="UREA.displayDate" :location="'bottom center'" :open-delay="500" :offset="-8">
      <template #activator="{ props: activatorProps }">
        <div
          v-bind="activatorProps"
          class="justify-self-start align-self-center p-3 text-body-1 w-100 cursor-pointer"
          @click="openInfobox"
        >
          {{ formatValue(UREA.displayValue, NumberFormat.DECIMAL) }}
        </div>
      </template>
    </VTooltip>

    <VTooltip
      v-if="isEdsEnabled"
      :text="edsTooltipText"
      location="bottom center"
      :open-delay="500"
      :offset="-8"
      :disabled="!hasAcceptedEdsLimitation"
    >
      <template #activator="{ props: activatorProps }">
        <div
          v-bind="activatorProps"
          data-testid="eds-column"
          class="justify-self-start align-self-center p-3 text-body-1 w-100"
        >
          <button
            v-if="!hasAcceptedEdsLimitation"
            data-testid="open-eds-sidebar-btn"
            class="mx-auto"
          >
            <VIcon
              class="material-icons-outlined"
              icon="visibility_off"
              size="18px"
              color="fg-disabled"
            />
          </button>
          <template v-else>
            {{ formatValue(edsScore?.scoreValue, NumberFormat.INTEGER) }}
          </template>
        </div>
      </template>
    </VTooltip>

    <div
      v-if="!isSidebarVisible"
      class="flex items-center justify-start gap-3 ml-[15px]"
      :class="{ 'pr-6': isInfoboxOpen }"
    >
      <ActionIcons
        :config="actionIconsConfig"
        @create-consultation-click="onCreateConsultation"
        @emergency-click="emit('emergency-click', caze.id)"
        @edit-patient-click="onEditPatient"
        @close-case-click="onCloseCase"
      />
    </div>
  </div>

  <CloseCaseModal
    :show="showCloseModal"
    @update:show="showCloseModal = $event"
    @confirm="handleCloseCaseConfirm"
  />

  <DividerLine class="col-span-full" />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import type { CaseWithPredictionResultDTO, ClinicalScoreResultDto, Discharge } from '@/generated'
import DividerLine from '@/atoms/DividerLine.vue'
import useFormattedPatientDetails from '@/composables/useFormattedPatientDetails'
import { useRouter } from 'vue-router'
import { routeNames } from '@/router/routeNames'
import { usePatientAkiParams } from '@/components/patient/composables/usePatientAkiParams'
import { useFormatValue } from '@/composables/useFormatValue'
import { NumberFormat } from '@/i18n/i18n'
import SystolicPressureDisplay from '@/components/patient/components/atoms/SystolicPressureDisplay.vue'
import { useEdsPlugin } from '@/composables/useEdsPlugin'
import { useAkiPlugin } from '@/composables/useAkiPlugin'
import { usePluginsStore } from '@/stores/plugins'
import ActionIcons from '@/components/patient/components/atoms/ActionIcons.vue'
import CloseCaseModal from '@/components/patient/components/molecules/CloseCaseModal.vue'
import { useUserStore } from '@/stores/user'
import { useNoticeStore } from '@/stores/notice'
import { NoticeType } from '@/stores/models/Notice'

const router = useRouter()
const { getFormattedPatientName } = useFormattedPatientDetails()
const { formatValue } = useFormatValue()
const { hasAcceptedEdsLimitation, getEdsScoreTooltipText } = useEdsPlugin()
const { getAkiPredictionTooltipText, hasAcceptedAkiLimitation } = useAkiPlugin()
const { isEdsEnabled, isConnectEnabled } = storeToRefs(usePluginsStore())

const userStore = useUserStore()
const { hasHospitalPhysicianRole, canCreateConsultations } = storeToRefs(userStore)
const { isConsultationDisabledForPatient } = userStore
const noticeStore = useNoticeStore()

const props = defineProps<{
  patientData: CaseWithPredictionResultDTO
  edsScore?: ClinicalScoreResultDto
  isSidebarVisible: boolean
  isSelected?: boolean
  isInfoboxOpen?: boolean
}>()

const rowClass = computed(() => (props.isSelected ? 'bg-utility-blue-50' : ''))

const emit = defineEmits<{
  (e: 'open-eds-sidebar'): void
  (e: 'open-aki-sidebar'): void
  (e: 'edit-patient', caseId: string): void
  (e: 'close-case', caseId: string, discharge: Discharge): void
  (e: 'emergency-click', caseId: string): void
  (e: 'open-infobox', caseId: string): void
}>()

function openInfobox() {
  emit('open-infobox', caze.value.id)
}

const caze = computed(() => props.patientData.case)

const showCloseModal = ref(false)

const isManuallyCreatedPatient = computed(() => {
  return caze.value.createdViaApi === true
})

const isConsultationDisabled = computed(() => {
  return isConsultationDisabledForPatient(caze.value.hospital?.id)
})

const actionIconsConfig = computed(() => ({
  canCreateConsultations: canCreateConsultations.value && isConnectEnabled.value,
  isConsultationDisabled: isConsultationDisabled.value,
  isEmergencyDisabled: caze.value.emergencyRoutingUnavailable,
  shouldAllowPatientActions:
    hasHospitalPhysicianRole.value &&
    canCreateConsultations.value &&
    isManuallyCreatedPatient.value,
  isManuallyCreatedPatient: isManuallyCreatedPatient.value
}))

async function onCreateConsultation() {
  try {
    await router.push({
      name: routeNames.NEW_CONSULTATION,
      query: { caseId: caze.value.id }
    })
  } catch (error) {
    console.error((error as Error).message)
    noticeStore.add({
      type: NoticeType.ERROR,
      message: 'Failed to navigate to consultation request'
    })
  }
}

function onEditPatient() {
  emit('edit-patient', caze.value.id)
}

function onCloseCase() {
  showCloseModal.value = true
}

async function handleCloseCaseConfirm(reason: Discharge) {
  emit('close-case', caze.value.id, reason)
}

const edsTooltipText = computed(() => getEdsScoreTooltipText(props.edsScore))

const reactiveAkiParams = computed(() => {
  return [
    ...(props.patientData?.usedInputs?.vitalSigns?.heartRate || []),
    ...(props.patientData?.usedInputs?.vitalSigns?.respiratoryRate || []),
    ...(props.patientData?.usedInputs?.vitalSigns?.invasiveBloodPressure?.meanArterialPressure ||
      []),
    ...(props.patientData?.usedInputs?.vitalSigns?.invasiveBloodPressure?.systolic || []),
    ...(props.patientData?.usedInputs?.vitalSigns?.invasiveBloodPressure?.diastolic || []),
    ...(props.patientData?.usedInputs?.vitalSigns?.nonInvasiveBloodPressure?.meanArterialPressure ||
      []),
    ...(props.patientData?.usedInputs?.vitalSigns?.nonInvasiveBloodPressure?.systolic || []),
    ...(props.patientData?.usedInputs?.vitalSigns?.nonInvasiveBloodPressure?.diastolic || []),
    ...(props.patientData?.usedInputs?.vitalSigns?.spo2 || []),
    ...(props.patientData?.usedInputs?.laboratory?.bilirubin || []),
    ...(props.patientData?.usedInputs?.laboratory?.crp || []),
    ...(props.patientData?.usedInputs?.laboratory?.creatinine || []),
    ...(props.patientData?.usedInputs?.laboratory?.ph || []),
    ...(props.patientData?.usedInputs?.laboratory?.lactate || []),
    ...(props.patientData?.usedInputs?.laboratory?.leukocytes || []),
    ...(props.patientData?.usedInputs?.laboratory?.potassium || []),
    ...(props.patientData?.usedInputs?.laboratory?.urea || [])
  ]
})

const { INV_BP, NON_INV_BP, HEART_RATE, RR, CREATININE, UREA } =
  usePatientAkiParams(reactiveAkiParams)

const akiRiskFlagStyle = computed(() => {
  const colorCode = props.patientData.predictionResult?.probabilityColorCode

  if (!colorCode) {
    return { backgroundColor: '#D1D5DB' }
  }

  return { backgroundColor: colorCode }
})

const akiPredictionTooltipText = computed(() =>
  getAkiPredictionTooltipText(props.patientData.predictionResult)
)

async function redirectToPatientDetails() {
  await router.push({
    name: routeNames.PATIENT_DETAILS,
    params: { caseId: props.patientData.case.id }
  })
}
</script>

<style scoped>
.aki-row {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  align-items: center;
}
</style>
